
import { IconDisplay } from "components/ui/icons"
import { useBaseModal, BaseModal } from "components/dialogs"
import { DDTViewer } from "components/datas"
import { ScreenEmpty } from "components/ui/screen"

import {
    Handle,
    Position,
  } from "@xyflow/react"

interface Port {
    id: string
    position: Position 
    label: string
    type: string
}

interface ObjectNodeProps {
  data: {
      id: string
      label: string
      type: string
      sourcePosition: Position
      targetPosition: Position
      ports?: Port[]
      sensorData?: Object
      deviceId?: string
      deviceType?: string
  }
}

const PORT_SiZE = 4
const START_PORT_SPACING = 36

interface PipeEdgeProps {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: string;
  targetPosition: string;
  style?: React.CSSProperties;
  markerEnd?: string;
}

// Минимальная реализация вычисления Bezier пути
/*function getBezierPath({
  sourceX,
  sourceY,
  targetX,
  targetY,
}: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}): string {
  // Расчет контрольной точки: простое среднее между source и target по X
  const controlX = (sourceX + targetX) / 2
  return `M${sourceX},${sourceY} C${controlX},${sourceY} ${controlX},${targetY} ${targetX},${targetY}`
}*/

function getStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
}: {
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
}): string {
    // Вычисляем промежуточную точку по X.
    // Такой вариант рисует линию от исходной точки до midX на уровне sourceY,
    // затем вертикальную линию до уровня targetY,
    // и горизонтальную линию до целевой точки.
    const midX = (sourceX + targetX) / 2
    return `M${sourceX},${sourceY} L${midX},${sourceY} L${midX},${targetY} L${targetX},${targetY}`
}

export function PipeEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: PipeEdgeProps) {
  const edgePath = getStepPath({ sourceX, sourceY, targetX, targetY })
  return (
      <path
          id={id}
          style={{
              ...style,
              stroke: '#8B4513',        // Цвет, похожий на металл или трубу
              strokeWidth: 5,           // Толщина линии, имитирующая трубу
              strokeDasharray: '30 1',   // Опционально: штриховка, если нужно
              fill: 'none',
          }}
          d={edgePath}
          markerEnd={markerEnd}
      />
  )
}

export const ObjectNode: React.FC<ObjectNodeProps> = ({ data }) => {
  const ports = data.ports || []
  const leftPorts = ports.filter(port => port.position === 'left')
  const rightPorts = ports.filter(port => port.position === 'right')
  const { open, handleOpen, handleClose } = useBaseModal()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
      title={data.label}
      className={`object-flow ${data?.type}`}
    >

      {leftPorts.map((port, index) => (
        <Handle
          key={port.id}
          title={port.label}
          type="target"
          position={Position.Left}
          id={port.id}
          style={{
            top: `${index * PORT_SiZE * 2 + (START_PORT_SPACING - leftPorts.length * PORT_SiZE)}px`,
            width: `${PORT_SiZE}px`,
            height: `${PORT_SiZE}px`,
            border: 0,
          }}
        />
      ))}

      <div onClick={data.deviceId ? handleOpen : undefined}>
        <IconDisplay iconType={data?.type || ''} sensorData={data.sensorData} />
      </div>
      <ScreenEmpty enabled={data.deviceId ? true : false}>
        <BaseModal 
          title={data.label} 
          open={open} 
          onClose={handleClose} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
              sx: {
                  height: '70vh',
                  maxHeight: '600px',
                  minHeight: '400px'
              }
          }}
        >
            <ScreenEmpty enabled={data.deviceType === "ddt"}> 
              <DDTViewer 
                  id={data.deviceId!} 
                  realtime={false} 
                  showDatePanel={false} 
                  showChartPanel={false} 
                  showRangeslider={false}
                  maxLimit={100}
              />
            </ScreenEmpty>
        </BaseModal>
      </ScreenEmpty>
      {rightPorts.map((port, index) => (
        <Handle
          key={port.id}
          title={port.label}
          type="source"
          position={Position.Right}
          id={port.id}
          style={{
            top: `${index * PORT_SiZE * 2 + (START_PORT_SPACING - rightPorts.length * PORT_SiZE)}px`,
            width: `${PORT_SiZE}px`,
            height: `${PORT_SiZE}px`,
            border: 0,
          }}
        />
      ))}
    </div>
  )
}

interface ObjectEdgeStyle {
    stroke: string
    strokeWidth: number
}

export function GetEdgeStyle(type?: string): ObjectEdgeStyle {
    switch(type) {
      case "gas_gts_grp":
        return {stroke: "#87CEEB", strokeWidth: 1} as ObjectEdgeStyle
      case "gas_gts_shrp":
        return {stroke: "#32CD32", strokeWidth: 1} as ObjectEdgeStyle
    }
    return {stroke: "#FFA500", strokeWidth: 1} as ObjectEdgeStyle
}
